import { useState } from 'react';
import { showToast } from 'Toast';
import { streamEditorApi } from './streamEditorApi';
import { entityMetadataFromForm, fromForm } from './formFieldConversions';
import { isNotAcceptableError, isNotLoggedInError, isNotPermittedError } from 'BackRoyalApi';
import { LOGGED_OUT_DESCRIPTION, messageFrom } from './serverErrors';
import { type PublishMeta, type StreamFormValues, type StreamRecord } from './StreamEditor.types';

// Long enough to read a server message. A save failure leaves edits in the form, so the admin must
// have time to see why.
const VALIDATION_TOAST_MS = 12000;

// How long the Save button reads 'Saved', as the Angular panel did.
const SAVED_FLASH_MS = 1000;

// Tells a failed metadata save apart from a course that has none to save.
const METADATA_SAVE_FAILED = Symbol('metadata save failed');

function statusOf(error: unknown) {
    return (error as { status?: number | string })?.status;
}

// A stale version needs a dialog rather than a toast, so it is reported through a callback instead
// of here. `metadataSaved` says whether the SEO metadata went first and succeeded, because the two
// requests are not one transaction and the admin has to know the course alone failed.
function reportSaveFailure(
    error: unknown,
    onStaleVersion: (message: string) => void,
    { metadataSaved }: { metadataSaved: boolean },
) {
    const status = statusOf(error);
    const message = messageFrom(error, 'The course was not saved.');
    const partialSave = metadataSaved ? ' The SEO metadata was saved.' : '';

    // Someone else saved this course while it was open. The edits are still in the form.
    // 406 also carries plain validation failures, so the message is what separates the two.
    if (isNotAcceptableError(error) && /more recent version/i.test(message)) {
        onStaleVersion(message);
        return;
    }

    if (isNotLoggedInError(error) || isNotPermittedError(error)) {
        showToast({
            message: 'The course was not saved',
            description: isNotLoggedInError(error)
                ? `${LOGGED_OUT_DESCRIPTION}${partialSave}`
                : `You are not allowed to publish or unpublish a course.${partialSave}`,
            type: 'danger',
            timeout: VALIDATION_TOAST_MS,
        });
        return;
    }

    // A network failure or a timeout. The SEO metadata may already have been written, and a
    // timed-out save may have committed.
    if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR' || status === 0) {
        showToast({
            message: 'The course may not have been saved',
            description: `The request did not finish. Your changes are still here. Reload the page before you edit further.${partialSave}`,
            type: 'danger',
            timeout: VALIDATION_TOAST_MS,
        });
        return;
    }

    showToast({
        message: 'The course was not saved',
        description: `${message}${partialSave}`,
        type: 'danger',
        timeout: VALIDATION_TOAST_MS,
    });
}

/*
    Saves the course, in the order the Angular panel used: the SEO metadata first, on its own
    request, and the course only if that succeeded.

    RTK Query does not go through Angular's $http, so none of this reaches ApiErrorHandler. Every
    failure here would otherwise show the admin nothing at all.
*/
export function useStreamSave({ record, onSaved }: { record: StreamRecord; onSaved: (updated: StreamRecord) => void }) {
    const [updateStream] = streamEditorApi.useUpdateStreamMutation();
    const [updateEntityMetadata] = streamEditorApi.useUpdateEntityMetadataMutation();
    const [saving, setSaving] = useState(false);
    const [justSaved, setJustSaved] = useState(false);
    const [staleVersion, setStaleVersion] = useState<string | null>(null);

    /*
        Returns what the server stored, so the form's new baseline holds the saved SEO fields.
        Returning only a flag here loses them: the course response carries three fields, so the
        baseline otherwise comes from the record the page loaded with, the fields on screen snap
        back, and the next save writes those stale values over the ones just stored.

        `undefined` means the course has no metadata to save. FAILED means the course save must not
        run, and the toast has already gone out.
    */
    async function saveEntityMetadata(values: StreamFormValues) {
        const entityMetadata = entityMetadataFromForm(values, record);
        if (!entityMetadata) return undefined;

        try {
            return await updateEntityMetadata({ record: entityMetadata }).unwrap();
        } catch (error) {
            // The course save never runs, so nothing was written. EntityMetadata requires a title,
            // which is the failure an admin is most likely to hit here.
            showToast({
                message: 'Nothing was saved',
                description: messageFrom(error, 'The SEO metadata is invalid, so the course was not saved.'),
                type: 'danger',
                timeout: VALIDATION_TOAST_MS,
            });
            return METADATA_SAVE_FAILED;
        }
    }

    async function save(values: StreamFormValues, meta: PublishMeta) {
        setSaving(true);
        let metadataSaved = false;

        try {
            const savedMetadata = await saveEntityMetadata(values);
            if (savedMetadata === METADATA_SAVE_FAILED) return;

            metadataSaved = !!savedMetadata;

            const saved = fromForm(values, record);
            const savedFields = await updateStream({ record: saved, meta }).unwrap();

            /*
                The new baseline is what was sent, not the record the page loaded with. Using the
                loaded record here would reset the form to the values from before the save, which
                reads as the save having been thrown away.

                The response carries updated_at, published_at and modified_at only, and the next
                save needs that updated_at or the server refuses it as a stale version. `lessons`
                comes back from the record, because the payload trims each lesson to its id and the
                key terms dialog and the summary lesson lists need the rest.
            */
            onSaved({
                ...saved,
                lessons: record.lessons,
                ...(savedMetadata ? { entity_metadata: savedMetadata } : {}),
                ...savedFields,
            });

            setJustSaved(true);
            window.setTimeout(() => setJustSaved(false), SAVED_FLASH_MS);
        } catch (error) {
            reportSaveFailure(error, setStaleVersion, { metadataSaved });
        } finally {
            setSaving(false);
        }
    }

    return { save, saving, justSaved, staleVersion, dismissStaleVersion: () => setStaleVersion(null) };
}
